import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Carousel,
  Card,
  Badge,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import {
  FaChalkboardTeacher,
  FaClock,
  FaCertificate,
  FaArrowRight,
  FaPlayCircle,
  FaStar,
  FaUsers,
  FaBookOpen,
  FaCalendar,
  FaUser,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import Testimonials from "../../components/public/Testimonials";
import FAQ from "../../components/public/FAQ";
import WebinarService from "../../services/webinarService";
import CourseService from "../../services/courseService";
import CourseCarousel from "../../components/common/CourseCarousel";
import "../../styles/home.css";

const Home = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [upcomingWebinars, setUpcomingWebinars] = useState([]);
  const [loadingWebinars, setLoadingWebinars] = useState(true);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  useEffect(() => {
    loadUpcomingWebinars();
    loadCourses();
  }, []);

  const loadUpcomingWebinars = async () => {
    try {
      const response = await WebinarService.getAllWebinars("upcoming", 3);
      setUpcomingWebinars(response.data || []);
    } catch (error) {
      console.error("Failed to load webinars:", error);
    } finally {
      setLoadingWebinars(false);
    }
  };

  const loadCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await CourseService.getAllCourses();
      let coursesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response && Array.isArray(response)) {
        coursesData = response;
      }
      setCourses(coursesData);
    } catch (error) {
      console.error("Error loading courses:", error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  const slides = [
    {
      title: "Welcome to Sensible Academy",
      subtitle: "Your Gateway to a Successful IT Career",
      description:
        "Industry-focused training programs designed to make you job-ready",
      image:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80",
      buttonText: "Explore Courses",
      buttonAction: () => navigate("/courses"),
    },
    {
      title: "Learn from Industry Experts",
      subtitle: "21+ Years of Combined Teaching Experience",
      description:
        "Our instructors are certified professionals with real-world experience",
      image:
        "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      buttonText: "About Us",
      buttonAction: () => navigate("/about"),
    },
    {
      title: "Flexible Learning Options",
      subtitle: "Classroom & Online Batches Available",
      description: "Choose what works best for your schedule",
      image:
        "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      buttonText: "View Courses",
      buttonAction: () => navigate("/courses"),
    },
  ];

  // Stats data
  const stats = [
    { number: "8760+", label: "Happy Students", icon: FaUsers },
    { number: "21+", label: "Expert Trainers", icon: FaChalkboardTeacher },
    { number: "100+", label: "Courses", icon: FaBookOpen },
    { number: "95%", label: "Success Rate", icon: FaStar },
  ];

  // Format date function
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format time function
  const formatTime = (time) => {
    if (!time) return "";
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="home-page">
      <PublicNavbar />

      {/* Full Screen Slider */}
      <Carousel
        activeIndex={index}
        onSelect={handleSelect}
        indicators={true}
        controls={true}
        interval={5000}
        pause={false}
        className="hero-slider"
      >
        {slides.map((slide, idx) => (
          <Carousel.Item key={idx}>
            <div
              className="slide-bg"
              style={{
                backgroundImage: `url(${slide.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                height: "90vh",
                position: "relative",
              }}
            >
              {/* Dark overlay for better text visibility */}
              <div className="slider-overlay"></div>

              <Container className="h-100 d-flex align-items-center position-relative">
                <Row className="w-100">
                  <Col lg={8} className="text-white">
                    <h1 className="display-3 fw-bold mb-3 animate__animated animate__fadeInUp">
                      {slide.title}
                    </h1>
                    <h3 className="mb-4 animate__animated animate__fadeInUp animate__delay-1s">
                      {slide.subtitle}
                    </h3>
                    <p className="lead mb-4 animate__animated animate__fadeInUp animate__delay-2s">
                      {slide.description}
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      className="me-3 animate__animated animate__fadeInUp animate__delay-3s slider-btn"
                      onClick={slide.buttonAction}
                    >
                      {slide.buttonText} <FaArrowRight className="ms-2" />
                    </Button>
                    <Button
                      variant="outline-light"
                      size="lg"
                      className="animate__animated animate__fadeInUp animate__delay-3s slider-btn-outline"
                      onClick={() => navigate("/register")}
                    >
                      <FaPlayCircle className="me-2" /> Join Now
                    </Button>
                  </Col>
                </Row>
              </Container>
            </div>
          </Carousel.Item>
        ))}
      </Carousel>

      {/* Stats Section */}
      <Container className="py-5">
        <Row className="g-4">
          {stats.map((stat, idx) => (
            <Col md={3} sm={6} key={idx}>
              <div className="stat-card text-center p-4">
                <div className="stat-icon mb-3">
                  <stat.icon size={40} className="text-primary" />
                </div>
                <h2 className="fw-bold text-primary mb-2">{stat.number}</h2>
                <p className="text-muted mb-0">{stat.label}</p>
              </div>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Features Section */}
      <Container className="py-5">
        <h2 className="text-center mb-5 display-5 fw-bold">
          <span className="text-primary">Why Choose</span> Sensible Academy?
        </h2>
        <Row>
          <Col md={4} className="mb-4">
            <div className="feature-card text-center p-4 shadow-sm rounded-3 h-100">
              <div className="feature-icon bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                <FaChalkboardTeacher size={40} className="text-primary" />
              </div>
              <h4 className="fw-bold mb-3">Expert Trainers</h4>
              <p className="text-muted">
                Learn from certified industry professionals with years of
                teaching experience.
              </p>
            </div>
          </Col>

          <Col md={4} className="mb-4">
            <div className="feature-card text-center p-4 shadow-sm rounded-3 h-100">
              <div className="feature-icon bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                <FaClock size={40} className="text-primary" />
              </div>
              <h4 className="fw-bold mb-3">Flexible Schedule</h4>
              <p className="text-muted">
                Choose from weekday, weekend, or online batches that fit your
                schedule.
              </p>
            </div>
          </Col>

          <Col md={4} className="mb-4">
            <div className="feature-card text-center p-4 shadow-sm rounded-3 h-100">
              <div className="feature-icon bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                <FaCertificate size={40} className="text-primary" />
              </div>
              <h4 className="fw-bold mb-3">Certified Programs</h4>
              <p className="text-muted">
                Get recognized certificates upon completion to boost your
                career.
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Course Carousel Section - Replaces Pricing Section */}
      {loadingCourses ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading courses...</span>
          </div>
          <p className="mt-2 text-muted">Loading courses...</p>
        </div>
      ) : courses.length > 0 ? (
        <CourseCarousel courses={courses} title="Popular Courses" />
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">No courses available at the moment.</p>
        </div>
      )}

      {/* Upcoming Webinars Section - NEW */}
      {!loadingWebinars && upcomingWebinars.length > 0 && (
        <section className="py-5 bg-light">
          <Container>
            <h2 className="text-center mb-3 display-5 fw-bold">
              <span className="text-primary">Upcoming</span> Free Webinars
            </h2>
            <p className="text-center text-muted mb-5 fs-5">
              Learn from industry experts at no cost. Limited seats available!
            </p>
            <Row className="g-4">
              {upcomingWebinars.map((webinar) => (
                <Col md={4} key={webinar.id}>
                  <Card className="h-100 shadow-sm border-0 webinar-card">
                    {webinar.featured_image && (
                      <Card.Img
                        variant="top"
                        src={webinar.featured_image}
                        style={{ height: "200px", objectFit: "cover" }}
                      />
                    )}
                    <Card.Body>
                      <Badge bg="danger" className="mb-2 px-3 py-2">
                        🔴 Free Webinar
                      </Badge>
                      <Card.Title className="h5 fw-bold mb-3">
                        {webinar.title}
                      </Card.Title>
                      <div className="text-muted small">
                        <div className="d-flex align-items-center mb-2">
                          <FaCalendar className="text-primary me-2" size={14} />
                          <span>{formatDate(webinar.webinar_date)}</span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <FaClock className="text-primary me-2" size={14} />
                          <span>
                            {formatTime(webinar.webinar_time)} |{" "}
                            {webinar.duration || "2 hours"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <FaUser className="text-primary me-2" size={14} />
                          <span>By {webinar.instructor_name}</span>
                        </div>
                      </div>
                      <p className="text-muted mt-3 small">
                        {webinar.short_description?.substring(0, 100)}...
                      </p>
                    </Card.Body>
                    <Card.Footer className="bg-white border-0 pb-4">
                      <Link to={`/webinars/${webinar.slug}`}>
                        <Button variant="primary" className="w-100">
                          Register Now{" "}
                          <FaArrowRight className="ms-2" size={12} />
                        </Button>
                      </Link>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="text-center mt-5">
              <Link to="/webinars">
                <Button variant="outline-primary" size="lg">
                  View All Webinars <FaArrowRight className="ms-2" />
                </Button>
              </Link>
            </div>
          </Container>
        </section>
      )}

      {/* Testimonials Section */}
      <Testimonials />
      {/* Faq section */}
      <FAQ />

      {/* CTA Section */}
      <div className="cta-section py-5">
        <Container className="text-center">
          <h2 className="display-5 fw-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="lead text-white mb-4">
            Join thousands of successful students who transformed their careers
            with us.
          </p>
          <Button
            variant="light"
            size="lg"
            className="me-3 cta-btn"
            onClick={() => navigate("/register")}
          >
            Register Now
          </Button>
          <Button
            variant="outline-light"
            size="lg"
            onClick={() => navigate("/contact")}
          >
            Contact Us
          </Button>
        </Container>
      </div>

      <Footer />
    </div>
  );
};

export default Home;